import { Crown, Settings2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { RoleBadge } from "./RoleBadge";
import { EffectiveRole } from "@/lib/team-roles";

interface Team {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  owner_id?: string;
  memberCount?: number;
  myRole?: EffectiveRole;
}

interface TeamListProps {
  teams: Team[];
  isLoading: boolean;
  onManageTeam: (id: string, name: string) => void;
}

export const TeamList = ({ teams, isLoading, onManageTeam }: TeamListProps) => {
  if (isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-28 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (teams.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-10 text-center">
        <Users className="mb-3 h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium">No teams yet</p>
        <p className="text-sm text-muted-foreground">Create your first team above to start collaborating.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {teams.map((team) => (
        <Card
          key={team.id}
          className="flex flex-col justify-between gap-4 p-4 transition-shadow hover:shadow-md"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="truncate font-semibold">{team.name}</h3>
              <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                <Users className="h-3.5 w-3.5" />
                {team.memberCount || 0} member{(team.memberCount || 0) === 1 ? "" : "s"}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {team.myRole === "owner" && <Crown className="h-4 w-4 text-primary" aria-label="You own this team" />}
              {team.myRole && <RoleBadge role={team.myRole} />}
            </div>
          </div>
          <Button size="sm" className="w-full" onClick={() => onManageTeam(team.id, team.name)}>
            <Settings2 className="mr-2 h-4 w-4" />
            Manage
          </Button>
        </Card>
      ))}
    </div>
  );
};
